import {
  Checkbox,
  ErrorList,
  getFontCss,
  HoverableLinkSizes,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import Button from 'ext-ui/components/Button/Button'
import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { CompanyNameFormFlatFields } from 'src/app/Onboarding/Onboarding'
import AppName from 'src/components/AppName/AppName'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import TestId from 'src/components/TestId/TestId'
import TextInputField, {
  TextInputFieldDesigns,
} from 'src/components/TextInputField/TextInputField'
import { translateSingleString, useTranslate } from 'src/localization/Trns'
import { CreditCardListModel } from 'src/models/creditCard.models'
import { UserListModel } from 'src/models/user.models'
import { termsUrl } from 'src/navigation/urls'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import colors from 'src/style/variables.json'
import { css } from 'styled-components'

interface OnboardingStepOneProps {
  currentStep: number
  totalSteps: number
  setStep: (val: number) => void
  user: UserListModel | undefined
  mostRecentCreditCard?: CreditCardListModel
  flatFields: CompanyNameFormFlatFields
  onFlatFieldChange: (
    fieldName: keyof CompanyNameFormFlatFields,
    value: CompanyNameFormFlatFields[keyof CompanyNameFormFlatFields],
  ) => void
  flatFieldsErrors: Partial<
    Record<keyof CompanyNameFormFlatFields, Array<React.ReactElement | string>>
  >
  setErrorsAndReturnDidPassValidation: () => boolean
  customColor?: boolean
}

const OnboardingStepOne: React.FC<OnboardingStepOneProps> = (props) => {
  const locale = useSelector(selectNullSafeLocaleFromState)
  const { analytics } = useAnalyticsContext()
  const appName = useSelector(selectAppName)
  const translate = useTranslate()

  const [confirmTerms, setConfirmTerms] = useState(false)
  const [confirmTermsError, setConfirmTermsError] = useState(false)
  const [confirmManage, setConfirmManage] = useState(false)
  const [confirmManageError, setConfirmManageError] = useState(false)

  const onOnboardingNameFormSubmit = (): void => {
    analytics?.track('[onboarding] company name - continue', {
      screen: '/onboarding/name',
      name: props.flatFields.companyLegalName,
      'T&C_checkbox': confirmTerms,
      manage_card_program_checkbox: confirmManage,
    })

    const companyNameFlatFieldsPassedValidation =
      props.setErrorsAndReturnDidPassValidation()

    if (!confirmTerms) {
      setConfirmTermsError(true)
    }

    if (!confirmManage) {
      setConfirmManageError(true)
    }

    if (
      companyNameFlatFieldsPassedValidation &&
      confirmTerms &&
      confirmManage
    ) {
      props.setStep(2)
    }
  }

  return (
    <div
      css={css`
        display: flex;
        flex-grow: 1;
      `}
    >
      <div
        css={css`
          padding: 80px;
          flex-grow: 1;
        `}
      >
        <div
          css={css`
            ${getFontCss(
              12,
              props.customColor
                ? colors.tokensColorJPM.JPMbrown500
                : styleConstants.colors.purple,
              styleConstants.fontFamilies.regular,
            )};
            margin-bottom: 20px;
          `}
        >
          {translate('Step ${currentStep} of ${totalSteps}', {
            currentStep: props.currentStep.toString(),
            totalSteps: props.totalSteps.toString(),
          })}
        </div>
        <div
          css={css`
            ${getFontCss(
              32,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.medium,
            )};
            margin-bottom: 20px;
          `}
        >
          {props.user?.firstName
            ? translate('Welcome to ${appName}, ${firstName}!', {
                appName: <AppName />,
                firstName: props.user.firstName,
              })
            : translate('Welcome to ${appName}', {
                appName: <AppName />,
              })}
        </div>
        <div
          css={css`
            ${getFontCss(
              16,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.regular,
            )};
            margin-bottom: 40px;
          `}
        >
          {translate('To start, please enter the legal name of your company.')}
        </div>
        <TextInputField
          id={'companyLegalName'}
          css={css`
            ${props.flatFieldsErrors['companyLegalName']
              ? css`
                  margin-bottom: 20px;
                `
              : css`
                  margin-bottom: 40px;
                `}
          `}
          fieldLabel={translate('Company Legal Name')}
          design={TextInputFieldDesigns.New}
          errors={props.flatFieldsErrors['companyLegalName']}
          placeholder={translateSingleString(
            locale,
            "Enter your company's legal name",
          )}
          value={props.flatFields['companyLegalName']}
          onChange={(val: string): void =>
            props.onFlatFieldChange('companyLegalName', val)
          }
        />
        <div
          css={css`
            display: flex;
          `}
        >
          <TestId testId={'confirmManageCheckbox'}>
            <Checkbox
              onClick={(): void => {
                setConfirmManage(!confirmManage)
                setConfirmManageError(false)
              }}
              isSelected={confirmManage}
              css={css`
                align-items: start;
                margin-bottom: 20px;
              `}
              isRequired={confirmManageError}
              label={
                <span
                  css={css`
                    ${getFontCss(
                      16,
                      themeConstants.colors.font_primary,
                      styleConstants.fontFamilies.regular,
                    )};
                  `}
                >
                  {translate(
                    "I confirm that I manage my company's card program",
                  )}
                </span>
              }
            />
          </TestId>
        </div>
        <div
          css={css`
            display: flex;
          `}
        >
          <TestId testId={'confirmTermsCheckbox'}>
            <Checkbox
              onClick={(): void => {
                setConfirmTerms(!confirmTerms)
                setConfirmTermsError(false)
              }}
              isSelected={confirmTerms}
              css={css`
                align-items: start;
                margin-bottom: 40px;
              `}
              isRequired={confirmTermsError}
              label={
                <span
                  css={css`
                    ${getFontCss(
                      16,
                      themeConstants.colors.font_primary,
                      styleConstants.fontFamilies.regular,
                    )};
                  `}
                >
                  {translate("I confirm I've read the ${termsAndConditions}", {
                    termsAndConditions: (
                      <HoverableLink
                        size={HoverableLinkSizes.Large}
                        href={termsUrl()}
                        underline
                        regular
                        ariaLabelSource={'Terms and Conditions'}
                      >
                        {translate('Terms and Conditions')}
                      </HoverableLink>
                    ),
                  })}
                </span>
              }
            />
          </TestId>
        </div>
        {(confirmTermsError || confirmManageError) && (
          <ErrorList
            errors={[
              confirmTermsError || confirmManageError
                ? translate('Please confirm the above before continuing')
                : '',
            ]}
          />
        )}
        <Button
          type="button"
          variant="primary"
          onClick={onOnboardingNameFormSubmit}
          testId="next-button"
        >
          {translate('Next: Company Info')}
        </Button>
      </div>
      <div
        css={css`
          border-radius: 0px 10px 10px 0px;
          max-width: 320px;
          ${props.customColor
            ? css`
                background: ${colors.tokensColorJPM['JPMbrown-brand-color']};
              `
            : css`
                background: linear-gradient(
                  27deg,
                  #4f3fdd 0.27%,
                  #588af2 100.75%
                );
                box-shadow: 4px 0 8px 0 rgba(0, 0, 0, 0.15) inset;
              `}
        `}
      >
        <div
          css={css`
            margin: 120px 40px;
          `}
        >
          <div
            css={css`
              ${getFontCss(
                20,
                styleConstants.colors.white,
                styleConstants.fontFamilies.medium,
              )};
              margin-bottom: 16px;
            `}
          >
            {translate("Why do we need your company's legal name?")}
          </div>
          <div
            css={css`
              ${getFontCss(
                14,
                styleConstants.colors.white,
                styleConstants.fontFamilies.regular,
              )};
              margin-bottom: 16px;
            `}
          >
            {translate(
              '${appName} must verify businesses to meet financial regulations, keep your account secure, and correctly link your business credit card accounts.',
              { appName: appName },
            )}
          </div>
          <div
            css={css`
              ${getFontCss(
                14,
                styleConstants.colors.white,
                styleConstants.fontFamilies.regular,
              )};
            `}
          >
            {translate(
              'Please check that the company name you enter here matches the name your bank or card issuer has on file.',
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default OnboardingStepOne
